import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteMeta } from 'vue-router'
import { i18n } from '@/plugins/i18n'
import DashboardLayoutVue from '@/layouts/dashboard.vue'
import { useAuthStore } from '@/stores/auth'
import { ability } from '@/plugins/casl'

interface IRouteMeta {
  title?: string
  permission?: string
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard/home'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/Login.vue'),
      meta: {
        title: 'routes.login',
        public: true,
        guestOnly: true
      } as RouteMeta & IRouteMeta
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/ForgotPassword.vue'),
      meta: {
        title: 'routes.forgot_password',
        public: true,
        guestOnly: true
      } as RouteMeta & IRouteMeta
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/pages/ResetPassword.vue'),
      meta: {
        title: 'routes.reset_password',
        public: true,
        guestOnly: true
      } as RouteMeta & IRouteMeta
    },
    {
      path: '/dashboard',
      component: DashboardLayoutVue,
      redirect: '/dashboard/home',
      meta: {
        title: 'routes.dashboard',
        requiresAuth: true
      },
      children: [
        {
          path: 'home',
          name: 'home',
          component: () => import('@/pages/Home.vue'),
          meta: {
            title: 'routes.home',
            requiresAuth: true
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'activities-management',
          name: 'activities_management_index',
          component: () => import('@/pages/activities-management/index.vue'),
          meta: {
            title: 'routes.activities',
            requiresAuth: true,
            permission: 'events.admin_events.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'tickets',
          name: 'tickets_index',
          component: () => import('@/pages/tickets/index.vue'),
          meta: {
            title: 'routes.tickets',
            requiresAuth: true,
            permission: 'events.admin_ticket_sales.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'tickets/details/:id',
          name: 'tickets-details',
          component: () => import('@/pages/tickets/details/[id].vue'),
          meta: {
            title: 'routes.ticket_details',
            requiresAuth: true,
            permission: 'events.admin_ticket_sales.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'coordinators',
          name: 'coordinators_index',
          component: () => import('@/pages/coordinators/index.vue'),
          meta: {
            title: 'routes.coordinators',
            requiresAuth: true,
            permission: 'events.admin_planners.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'coordinators/details/:id',
          name: 'coordinators-details',
          component: () => import('@/pages/coordinators/details/[id].vue'),
          meta: {
            title: 'routes.coordinator_details',
            requiresAuth: true,
            permission: 'events.admin_planners.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'event-categories',
          name: 'event_categories_index',
          component: () => import('@/pages/event-categories/index.vue'),
          meta: {
            title: 'routes.event_categories',
            requiresAuth: true,
            permission: 'events.admin_categories.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'permissions',
          name: 'permissions_index',
          component: () => import('@/pages/permissions/index.vue'),
          meta: {
            title: 'routes.permissions',
            requiresAuth: true,
            permission: 'events.admin_permissions.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'roles',
          name: 'roles_index',
          component: () => import('@/pages/roles/index.vue'),
          meta: {
            title: 'routes.roles',
            requiresAuth: true,
            permission: 'events.admin_roles.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'supervisors',
          name: 'supervisors_index',
          component: () => import('@/pages/supervisors/index.vue'),
          meta: {
            title: 'routes.supervisors',
            requiresAuth: true,
            permission: 'users.admin_users.read'
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'supervisors/details/:id',
          name: 'supervisors-details',
          component: () => import('@/pages/supervisors/details/[id].vue'),
          meta: {
            title: 'routes.supervisor_details',
            requiresAuth: true,
            permission: 'users.admin_users.read'
          } as RouteMeta & IRouteMeta
        }
      ]
    },
    {
      path: '/:pathMatch(.*)',
      name: 'not-found',
      component: () => import('@/pages/404.vue'),
      meta: {
        title: 'routes.not_found'
      } as RouteMeta & IRouteMeta
    }
  ]
})

router.beforeEach((to) => {
  const authStore = useAuthStore()
  const isAuthed = authStore.isAuthenticated

  if (to.meta?.guestOnly && isAuthed) {
    return { path: '/dashboard/home' }
  }

  if (to.meta?.requiresAuth && !isAuthed) {
    return { path: '/login' }
  }

  const perm = (to.meta as (RouteMeta & IRouteMeta) | undefined)?.permission
  if (perm && isAuthed) {
    const i = perm.lastIndexOf('.')
    const action = perm.slice(i + 1)
    const subject = perm.slice(0, i)
    if (!ability.can(action, subject)) {
      return { path: '/dashboard/home' }
    }
  }

  return true
})

const applyRouteTitle = (loc: { meta?: RouteMeta }) => {
  const titleKey = (loc.meta as (RouteMeta & IRouteMeta) | undefined)?.title
  document.title = titleKey ? i18n.global.t(titleKey) : ''
}

router.afterEach((loc) => {
  applyRouteTitle(loc)
})

watch(
  () => i18n.global.locale.value,
  () => {
    applyRouteTitle(router.currentRoute.value)
  }
)

export default router
